import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { apiClient, useAuth } from "@/contexts/AuthContext";
import { SetuMark } from "@/components/Logo";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling", attempts: 0, data: null, error: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", error: "No session_id in URL" }); return; }
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 12; // 12 * 2s = 24s
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await apiClient().get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refreshUser();
          setState({ status: "paid", attempts, data });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState({ status: "failed", attempts, data });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", attempts, data });
          return;
        }
        setState({ status: "polling", attempts, data });
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setState({ status: "error", error: e.response?.data?.detail || e.message });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen grid place-items-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full border border-border rounded-lg p-8 bg-card">
        <Link to="/" className="flex items-center gap-2.5 mb-6">
          <div className="w-8 h-8 rounded-md bg-primary grid place-items-center text-primary-foreground"><SetuMark size={22} strokeWidth={2.6} /></div>
          <span className="font-heading text-lg tracking-tight">Setubuild.OS</span>
        </Link>

        {state.status === "polling" && (
          <div className="text-center py-4">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-primary" />
            <h1 className="font-display text-3xl mt-6">Confirming payment…</h1>
            <p className="text-muted-foreground text-sm mt-2">Attempt {state.attempts} of 12 · usually done in 5 seconds</p>
          </div>
        )}

        {state.status === "paid" && (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 grid place-items-center">
              <CheckCircle2 className="w-9 h-9 text-emerald-500" />
            </div>
            <h1 className="font-display text-3xl mt-6" data-testid="payment-success-title">Payment successful!</h1>
            <p className="text-muted-foreground text-sm mt-2">Welcome to <strong className="text-foreground">{state.data?.plan?.toUpperCase()}</strong> · {state.data?.cycle} plan</p>
            <button onClick={() => navigate("/app")} data-testid="go-to-dashboard-btn"
              className="btn-sharp mt-8 w-full bg-primary text-primary-foreground py-3 font-medium">
              Go to Dashboard
            </button>
          </div>
        )}

        {(state.status === "failed" || state.status === "timeout" || state.status === "error") && (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-500/10 grid place-items-center">
              <XCircle className="w-9 h-9 text-red-500" />
            </div>
            <h1 className="font-display text-3xl mt-6">
              {state.status === "timeout" ? "Still confirming…" : "Payment issue"}
            </h1>
            <p className="text-muted-foreground text-sm mt-2">
              {state.status === "timeout"
                ? "This is taking longer than usual. Your subscription will activate as soon as the payment is confirmed. Check back in a minute."
                : state.error || "Something went wrong. Please try again or contact support."}
            </p>
            <Link to="/app/subscription" className="btn-sharp mt-8 w-full inline-flex items-center justify-center bg-primary text-primary-foreground py-3 font-medium">
              Back to Plans
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
